import { Fragment, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePaymentTerms, useCreatePaymentTerm, useUpdatePaymentTerm, useDeletePaymentTerm } from '../../hooks/useLeads';
import { Button } from '../../components/common/Button';

// MKS-255 — `days` is a whole number of days until the invoice is due, so the
// backend now rejects anything outside 0–365 with a 400. Validate the same here
// so the admin sees why, instead of the form swallowing the click: the old
// `parseInt` + `!days || days <= 0` check silently dropped 0, -5 and 1.5 (which
// parses to 1) with no feedback at all.
export const MIN_PAYMENT_TERM_DAYS = 0;
export const MAX_PAYMENT_TERM_DAYS = 365;
const DAYS_ERROR = `Days must be a whole number between ${MIN_PAYMENT_TERM_DAYS} and ${MAX_PAYMENT_TERM_DAYS}.`;
const NAME_ERROR = 'Name is required.';

type DaysValidation = { days: number; error: null } | { days: null; error: string };

function validateDays(raw: string): DaysValidation {
  const trimmed = raw.trim();
  // Digits only: a sign or a decimal point is rejected outright rather than
  // truncated, so "1.5" is an error and never becomes 1.
  if (!/^\d+$/.test(trimmed)) return { days: null, error: DAYS_ERROR };
  const days = Number(trimmed);
  if (days < MIN_PAYMENT_TERM_DAYS || days > MAX_PAYMENT_TERM_DAYS) {
    return { days: null, error: DAYS_ERROR };
  }
  return { days, error: null };
}

export default function PaymentTermsPage() {
  const navigate = useNavigate();
  const { data, isLoading } = usePaymentTerms();
  const createMutation = useCreatePaymentTerm();
  const updateMutation = useUpdatePaymentTerm();
  const deleteMutation = useDeletePaymentTerm();

  const [newName, setNewName] = useState('');
  const [newDays, setNewDays] = useState('');
  const [editId, setEditId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [editDays, setEditDays] = useState('');
  const [newError, setNewError] = useState<string | null>(null);
  const [editError, setEditError] = useState<string | null>(null);

  const terms = data?.data ?? [];

  const startEdit = (term: { id: number; name: string; days: number }) => {
    setEditId(term.id);
    setEditName(term.name);
    setEditDays(String(term.days));
    setEditError(null);
  };

  const handleCreate = () => {
    if (!newName.trim()) {
      setNewError(NAME_ERROR);
      return;
    }
    const { days, error } = validateDays(newDays);
    if (error !== null) {
      setNewError(error);
      return;
    }
    setNewError(null);
    createMutation.mutate({ name: newName.trim(), days }, {
      onSuccess: () => { setNewName(''); setNewDays(''); },
    });
  };

  const handleUpdate = () => {
    if (editId === null) return;
    if (!editName.trim()) {
      setEditError(NAME_ERROR);
      return;
    }
    const { days, error } = validateDays(editDays);
    if (error !== null) {
      setEditError(error);
      return;
    }
    setEditError(null);
    updateMutation.mutate({ id: editId, name: editName.trim(), days }, {
      onSuccess: () => setEditId(null),
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <button
          onClick={() => navigate('/admin')}
          className="text-sm text-gray-500 hover:text-gray-700 mb-1"
        >
          &larr; Back to Admin
        </button>
        <h1 className="text-2xl font-semibold text-gray-900">Payment Terms</h1>
        <p className="text-sm text-gray-500">Configure payment term options available for leads.</p>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Days</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {isLoading && (
              <tr><td colSpan={3} className="px-6 py-4 text-sm text-gray-400">Loading...</td></tr>
            )}
            {terms.map((term) => (
              <Fragment key={term.id}>
                <tr>
                  {editId === term.id ? (
                    <>
                      <td className="px-6 py-2">
                        <input
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
                        />
                      </td>
                      <td className="px-6 py-2">
                        <input
                          type="number"
                          min={MIN_PAYMENT_TERM_DAYS}
                          max={MAX_PAYMENT_TERM_DAYS}
                          step={1}
                          value={editDays}
                          onChange={(e) => setEditDays(e.target.value)}
                          className="w-20 rounded border border-gray-300 px-2 py-1 text-sm"
                        />
                      </td>
                      <td className="px-6 py-2 text-right space-x-2">
                        <Button size="sm" onClick={handleUpdate} isLoading={updateMutation.isPending}>Save</Button>
                        <Button size="sm" variant="secondary" onClick={() => { setEditId(null); setEditError(null); }}>Cancel</Button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="px-6 py-3 text-sm text-gray-900">{term.name}</td>
                      <td className="px-6 py-3 text-sm text-gray-500">{term.days}</td>
                      <td className="px-6 py-3 text-right space-x-2">
                        <button
                          onClick={() => startEdit(term)}
                          className="text-sm text-primary-600 hover:text-primary-800"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => { if (confirm(`Delete "${term.name}"?`)) deleteMutation.mutate(term.id); }}
                          className="text-sm text-red-600 hover:text-red-800"
                        >
                          Delete
                        </button>
                      </td>
                    </>
                  )}
                </tr>
                {editId === term.id && editError && (
                  <tr>
                    <td colSpan={3} className="px-6 pb-2 text-sm text-red-600" role="alert">{editError}</td>
                  </tr>
                )}
              </Fragment>
            ))}
            {/* Add new row */}
            <tr>
              <td className="px-6 py-2">
                <input
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="e.g. Net 120"
                  className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
                />
              </td>
              <td className="px-6 py-2">
                <input
                  type="number"
                  min={MIN_PAYMENT_TERM_DAYS}
                  max={MAX_PAYMENT_TERM_DAYS}
                  step={1}
                  value={newDays}
                  onChange={(e) => setNewDays(e.target.value)}
                  placeholder="120"
                  className="w-20 rounded border border-gray-300 px-2 py-1 text-sm"
                />
              </td>
              <td className="px-6 py-2 text-right">
                <Button size="sm" onClick={handleCreate} isLoading={createMutation.isPending} disabled={!newName.trim() || !newDays}>
                  Add
                </Button>
              </td>
            </tr>
            {newError && (
              <tr>
                <td colSpan={3} className="px-6 pb-2 text-sm text-red-600" role="alert">{newError}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
